// src/pages/useradmin/maintenance/MyMaintenance.jsx

import React, { useState, useRef, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { MdBuild, MdCheckCircle, MdPending, MdWarning } from "react-icons/md";
import { FiMoreVertical, FiEye, FiAlertCircle, FiEdit, FiTrash2, FiX, FiCheckCircle } from "react-icons/fi";
import { Form, Button } from "react-bootstrap";

import UserMasterLayout from "../../../layout/useradmin/UserMasterLayout";
import AppPagination from "../../../components/general/pagination/Pagination.js";
import "../../../assets/css/useradmin/maintenance/UserMaintenance.css";

const MyMaintenance = () => {
  const navigate = useNavigate();

  // ── States ──
  const [localRecords, setLocalRecords] = useState([
    { id: 1, equipment: "John Deere 5050D Tractor", issue: "Engine overheating",  requestDate: "2026-01-05", cost: 4500, status: "Pending"     },
    { id: 2, equipment: "Rotavator 7ft",             issue: "Blade replacement",   requestDate: "2026-01-02", cost: 3000, status: "Pending"     },
    { id: 3, equipment: "Combine Harvester",         issue: "Hydraulic leakage",   requestDate: "2025-12-28", cost: 0,    status: "In Progress" },
    { id: 4, equipment: "Seed Drill 9-Tyne",         issue: "Calibration issue",   requestDate: "2026-01-07", cost: 2500, status: "Completed"   },
  ]);

  const mechanics = ["Ali Khan", "Sara Ahmed", "Usman Riaz"];

  const [activeMenu,       setActiveMenu]       = useState(null);
  const [currentPage,   setCurrentPage]   = useState(1);
  const recordsPerPage = 5;
  const totalPages     = Math.ceil(localRecords.length / recordsPerPage);
  const currentRecords = localRecords.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);
  const [showViewModal,    setShowViewModal]    = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showUpdateModal,  setShowUpdateModal]  = useState(false);
  const [selectedRecord,   setSelectedRecord]   = useState(null);

  const [updatedStatus,    setUpdatedStatus]    = useState('');
  const [completedRecords, setCompletedRecords] = useState({});
  const [updateSaved,      setUpdateSaved]      = useState(false);
  const [reviewText,       setReviewText]       = useState('');
  const [reviews,          setReviews]          = useState({});
  const [rating,           setRating]           = useState(0);

  const [disputeTickets,   setDisputeTickets]   = useState([]);
  const [selectedTicket,   setSelectedTicket]   = useState(null);
  const [chatMessages,     setChatMessages]     = useState({});
  const [currentMessage,   setCurrentMessage]   = useState('');

  // ── Effects ──
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest('.um-actions-wrapper')) setActiveMenu(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // ── Stats ──
  const stats = [
    { icon: <MdBuild />,       value: localRecords.length,                                          label: "Total Requests", color: "#FDB714", bg: "#fff8e6"  },
    { icon: <MdWarning />,     value: localRecords.filter(r => r.status === 'In Progress').length,  label: "In Progress",    color: "#FDB714", bg: "#fff8e6"  },
    { icon: <MdCheckCircle />, value: localRecords.filter(r => r.status === 'Completed').length,   label: "Completed",      color: "#FDB714", bg: "#fff8e6"  },
    { icon: <MdPending />,     value: localRecords.filter(r => r.status === 'Pending').length,      label: "Pending",        color: "#FDB714", bg: "#fff8e6"  },
  ];

  // ── Status Helper ──
  const getStatusStyle = (status) => {
    if (status === 'Completed')  return { bg: '#D1FAE5', color: '#065F46' };
    if (status === 'In Progress')return { bg: '#DBEAFE', color: '#1D4ED8' };
    if (status === 'Cancelled')  return { bg: '#FEE2E2', color: '#991B1B' };
    return { bg: '#FEF3C7', color: '#92400E' }; // Pending
  };

  // ── Functions ──
  const handleView = (record) => { setSelectedRecord(record); setShowViewModal(true); setActiveMenu(null); };

  const handleDispute = (record) => { setSelectedRecord(record); setShowDisputeModal(true); setActiveMenu(null); };

  const handleUpdate = (record) => {
    setSelectedRecord(record);
    setUpdatedStatus(record.status || 'Pending');
    setUpdateSaved(false);
    setReviewText('');
    setRating(0);
    setShowUpdateModal(true);
    setActiveMenu(null);
  };

  const handleDelete = (record) => {
    setActiveMenu(null);
    if (window.confirm('Are you sure you want to delete this record?')) {
      setLocalRecords(localRecords.filter(r => r.id !== record.id));
    }
  };

  const handleUpdateSubmit = (e) => {
    e.preventDefault();
    const updated = localRecords.map(r => r.id === selectedRecord.id ? { ...r, status: updatedStatus } : r);
    setLocalRecords(updated);
    if (updatedStatus === 'Completed') {
      setCompletedRecords(prev => ({ ...prev, [selectedRecord.id]: true }));
      setUpdateSaved(true);
    } else {
      setShowUpdateModal(false);
      alert('Status updated successfully!');
    }
  };

  const handleReviewSubmit = (e) => {
    e.preventDefault();
    setReviews(prev => ({ ...prev, [selectedRecord.id]: { text: reviewText, rating } }));
    setReviewText('');
    setRating(0);
    setUpdateSaved(false);
    setShowUpdateModal(false);
    alert('Review submitted successfully!');
  };

  const handleDisputeSubmit = (e) => {
    e.preventDefault();
    const formData  = new FormData(e.target);
    const newTicket = {
      id:          Date.now(),
      raisedBy:    formData.get('raisedBy'),
      against:     formData.get('against'),
      category:    formData.get('category'),
      date:        formData.get('date'),
      description: formData.get('description'),
      equipment:   selectedRecord?.equipment || 'N/A',
      issue:       selectedRecord?.issue     || 'N/A',
      status:      'Open',
      createdAt:   new Date().toLocaleString()
    };
    setDisputeTickets([...disputeTickets, newTicket]);
    setChatMessages({ ...chatMessages, [newTicket.id]: [] });
    setSelectedTicket(newTicket);
    e.target.reset();
    alert('Dispute ticket submitted successfully!');
  };

  const handleSendMessage = () => {
    if (!currentMessage.trim() || !selectedTicket) return;
    const newMsg = {
      id:        Date.now(),
      sender:    'User',
      message:   currentMessage,
      timestamp: new Date().toLocaleTimeString()
    };
    setChatMessages({
      ...chatMessages,
      [selectedTicket.id]: [...(chatMessages[selectedTicket.id] || []), newMsg]
    });
    setCurrentMessage('');
  };

  // ================================================================
  // RETURN
  // ================================================================

  return (
    <UserMasterLayout>
      <div className="um-page">

        {/* ── Page Header ── */}
        <div className="um-page-header">
          <h2 className="um-page-title">My Maintenance</h2>
          <button className="um-request-btn" onClick={() => navigate("/mechanics")}>
            + Maintenance Request
          </button>
        </div> 

        {/* ════════════════════════════════
            Stat Cards — MyEquipment style
        ════════════════════════════════ */}
        <Row className="mb-4">
          {stats.map((s, i) => (
            <Col md={3} key={i}>
              <div className="um-stat-card" style={{ borderLeft: `4px solid ${s.color}` }}>
                <span className="um-stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</span>
                <h3>{s.value}</h3>
                <p>{s.label}</p>
              </div>
            </Col>
          ))}
        </Row>

        {/* ════════════════════════════════
            Table — MyEquipment style
        ════════════════════════════════ */}
        <div className="um-table-section">
          <div className="um-table-header">
            <h3 className="um-table-title">Maintenance Records</h3>
          </div>

          <div className="um-table-wrapper">
            <table className="um-table">
              <thead>
                <tr>
                  <th>FULL NAME</th>
                  <th>MACHINERY TYPE</th>
                  <th>PREFERRED DATE</th>
                  <th>LOCATION</th>
                  <th>ISSUE DESCRIPTION</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {currentRecords.map((r) => {
                  const st        = getStatusStyle(r.status);
                  const isComp    = completedRecords[r.id];
                  const hasReview = reviews[r.id];
                  return (
                    <tr key={r.id}>
                      <td className="um-name">{r.fullName || "John Smith"}</td>
                      <td>{r.equipment}</td>
                      <td>{r.preferredDate || r.requestDate}</td>
                      <td>{r.location || "Lahore"}</td>
                      <td className="um-issue">{r.issue}</td>
                      <td>
                        <div className="um-status-cell">
                          <span className="um-status-badge" style={{ background: st.bg, color: st.color }}>
                            {r.status || 'Pending'}
                          </span>
                          {isComp && !hasReview && (
                            <button className="um-review-btn"
                              onClick={() => { setSelectedRecord(r); setShowUpdateModal(true); setUpdateSaved(true); }}>
                              ⭐ Review
                            </button>
                          )}
                          {hasReview && (
                            <span className="um-review-stars">
                              {'★'.repeat(reviews[r.id].rating)}
                              {'☆'.repeat(5 - reviews[r.id].rating)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="um-actions-cell">
                        <div className="um-actions-wrapper">
                          <button className="um-menu-btn"
                            onClick={() => setActiveMenu(activeMenu === r.id ? null : r.id)}>
                            <FiMoreVertical size={20} />
                          </button>
                          {activeMenu === r.id && (
                            <div className="um-dropdown">
                              <button className="um-drop-item um-drop-view"    onClick={() => handleView(r)}>
                                <FiEye size={14} /> View
                              </button>
                              <button className="um-drop-item um-drop-dispute" onClick={() => handleDispute(r)}>
                                <FiAlertCircle size={14} /> Dispute
                              </button>
                              <button className="um-drop-item um-drop-update"  onClick={() => handleUpdate(r)}>
                                <FiEdit size={14} /> Update
                              </button>
                              <button className="um-drop-item um-drop-delete"  onClick={() => handleDelete(r)}>
                                <FiTrash2 size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <AppPagination
  currentPage={currentPage}
  totalPages={totalPages}
  totalItems={localRecords.length}
  itemsPerPage={recordsPerPage}
  onPageChange={(page) => setCurrentPage(page)}
/>
        </div>

      </div>

      {/* ════════════════════════════════
          VIEW MODAL — PayoutsManagement style
      ════════════════════════════════ */}
      {showViewModal && selectedRecord && (
        <div className="um-overlay" onClick={() => setShowViewModal(false)}>
          <div className="um-modal" onClick={e => e.stopPropagation()}>

            <div className="um-modal-header">
              <div className="um-modal-header-left">
                <h5>Maintenance Detail</h5>
                <span>Full breakdown of this maintenance request</span>
              </div>
              <button className="um-modal-close" onClick={() => setShowViewModal(false)}><FiX /></button>
            </div>

            <div className="um-modal-body">
              <div className="um-modal-status-bar">
                <div className="um-sb-left">
                  <span className="um-sb-id">#{selectedRecord.id} — {selectedRecord.equipment}</span>
                  <span className="um-status-badge"
                    style={{ background: getStatusStyle(selectedRecord.status).bg, color: getStatusStyle(selectedRecord.status).color }}>
                    {selectedRecord.status || 'Pending'}
                  </span>
                </div>
              </div>

              <p className="um-section-title">Request Information</p>
              <div className="um-info-grid">
                <div className="um-info-item"><label>Full Name</label><span>{selectedRecord.fullName || 'John Smith'}</span></div>
                <div className="um-info-item"><label>Machinery Type</label><span>{selectedRecord.equipment}</span></div>
                <div className="um-info-item"><label>Preferred Date</label><span>{selectedRecord.preferredDate || selectedRecord.requestDate}</span></div>
                <div className="um-info-item"><label>Location</label><span>{selectedRecord.location || 'Lahore'}</span></div>
                <div className="um-info-item"><label>Cost</label><span className="um-price">{selectedRecord.cost > 0 ? `Rs. ${selectedRecord.cost.toLocaleString()}` : 'Free'}</span></div>
                <div className="um-info-item"><label>Status</label>
                  <span className="um-status-badge"
                    style={{ background: getStatusStyle(selectedRecord.status).bg, color: getStatusStyle(selectedRecord.status).color }}>
                    {selectedRecord.status || 'Pending'}
                  </span>
                </div>
              </div>

              <p className="um-section-title">Issue Description</p>
              <div className="um-notes-box">{selectedRecord.issue}</div>
            </div>

            <div className="um-modal-footer">
              <button className="um-mf-btn um-mf-dispute" onClick={() => { setShowViewModal(false); handleDispute(selectedRecord); }}>
                <FiAlertCircle size={14} /> Raise Dispute
              </button>
              <button className="um-mf-btn um-mf-update" onClick={() => { setShowViewModal(false); handleUpdate(selectedRecord); }}>
                <FiEdit size={14} /> Update Status
              </button>
              <button className="um-mf-btn um-mf-close" onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════
          UPDATE STATUS MODAL — PayoutsManagement style
      ════════════════════════════════ */}
      {showUpdateModal && (
        <div className="um-overlay" onClick={() => { setShowUpdateModal(false); setUpdateSaved(false); }}>
          <div className="um-modal um-modal-sm" onClick={e => e.stopPropagation()}>

            <div className="um-modal-header">
              <div className="um-modal-header-left">
                <h5>Update Status</h5>
                <span>{selectedRecord?.equipment || ''}</span>
              </div>
              <button className="um-modal-close" onClick={() => { setShowUpdateModal(false); setUpdateSaved(false); }}><FiX /></button>
            </div>

            <div className="um-modal-body">
              {!updateSaved ? (
                <Form onSubmit={handleUpdateSubmit}>
                  <Form.Group className="mb-3">
                    <label className="um-form-label">Current Status</label>
                    <div className="um-current-status">
                      <span className="um-status-badge"
                        style={{ background: getStatusStyle(selectedRecord?.status).bg, color: getStatusStyle(selectedRecord?.status).color }}>
                        {selectedRecord?.status || 'Pending'}
                      </span>
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <label className="um-form-label">Update To</label>
                    <select className="um-form-select" value={updatedStatus} onChange={e => setUpdatedStatus(e.target.value)} required>
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </Form.Group>
                  <button type="submit" className="um-save-btn" style={{ width: '100%' }}>Save Changes</button>
                </Form>
              ) : (
                <div className="um-review-section">
                  <div className="um-success-msg">✅ Status marked as <strong>Completed!</strong> Share your experience below.</div>
                  <h6 className="um-review-heading">⭐ Write a Review</h6>
                  <Form onSubmit={handleReviewSubmit}>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Rating</label>
                      <div className="um-star-row">
                        {[1,2,3,4,5].map(star => (
                          <span key={star} onClick={() => setRating(star)}
                            className={`um-star ${star <= rating ? 'um-star-filled' : 'um-star-empty'}`}>★</span>
                        ))}
                      </div>
                      {rating === 0 && <small className="um-rating-error">Please select a rating</small>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Your Review</label>
                      <textarea className="um-form-textarea"
                        rows={3} placeholder="Share your experience..."
                        value={reviewText} onChange={e => setReviewText(e.target.value)} required />
                    </Form.Group>
                    <div className="um-review-btns">
                      <button type="submit" disabled={rating === 0} className={`um-save-btn ${rating === 0 ? 'um-btn-disabled' : ''}`}>
                        Submit Review
                      </button>
                      <button type="button" className="um-skip-btn" onClick={() => { setShowUpdateModal(false); setUpdateSaved(false); }}>
                        Skip for Now
                      </button>
                    </div>
                  </Form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════
          DISPUTE MODAL — PayoutsManagement style
      ════════════════════════════════ */}
      {showDisputeModal && (
        <div className="um-overlay" onClick={() => setShowDisputeModal(false)}>
          <div className="um-modal um-modal-xl" onClick={e => e.stopPropagation()}>

            <div className="um-modal-header">
              <div className="um-modal-header-left">
                <h5>Maintenance Dispute Management</h5>
                <span>Create and track dispute tickets</span>
              </div>
              <button className="um-modal-close" onClick={() => setShowDisputeModal(false)}><FiX /></button>
            </div>

            <div className="um-modal-body um-dispute-body">
              <div className="um-dispute-layout">

                {/* Left — Form */}
                <div className="um-dispute-left">
                  <p className="um-section-title">Create Dispute Ticket</p>
                  <Form onSubmit={handleDisputeSubmit}>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Raised By</label>
                      <input className="um-form-input" type="text" name="raisedBy" defaultValue="John Smith" required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Against</label>
                      <select className="um-form-select" name="against" required>
                        {mechanics.map((m, i) => <option key={i} value={m}>{m}</option>)}
                      </select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Equipment</label>
                      <input className="um-form-input" type="text" name="equipment" value={selectedRecord?.equipment || 'N/A'} readOnly />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Issue</label>
                      <input className="um-form-input" type="text" name="issue" value={selectedRecord?.issue || 'N/A'} readOnly />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Category</label>
                      <select className="um-form-select" name="category" required>
                        <option value="">Select Category</option>
                        <option value="Service Delay">Service Delay</option>
                        <option value="Poor Repair Quality">Poor Repair Quality</option>
                        <option value="Overcharging">Overcharging</option>
                        <option value="Equipment Damage">Equipment Damage</option>
                        <option value="Other">Other</option>
                      </select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Date</label>
                      <input className="um-form-input" type="date" name="date" defaultValue={new Date().toISOString().split('T')[0]} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <label className="um-form-label">Description</label>
                      <textarea className="um-form-textarea" name="description" rows={3} placeholder="Describe the issue in detail..." required />
                    </Form.Group>
                    <button type="submit" className="um-save-btn" style={{ width: '100%' }}>Submit Dispute Ticket</button>
                  </Form>
                </div>

                {/* Right — Tickets & Chat */}
                <div className="um-dispute-right">
                  <p className="um-section-title">Dispute Tickets</p>
                  {disputeTickets.length === 0 ? (
                    <div className="um-empty-state">No dispute tickets yet. Submit a ticket to get started.</div>
                  ) : (
                    <div className="um-tickets-list">
                      {disputeTickets.map(ticket => (
                        <div key={ticket.id}
                          className={`um-ticket-card ${selectedTicket?.id === ticket.id ? 'um-ticket-selected' : ''}`}
                          onClick={() => setSelectedTicket(ticket)}>
                          <div className="um-ticket-header">
                            <strong className="um-ticket-id">#{ticket.id}</strong>
                            <span className={`um-ticket-status ${ticket.status === 'Open' ? 'open' : 'closed'}`}>{ticket.status}</span>
                          </div>
                          <div className="um-ticket-body">
                            <div><strong>Equipment:</strong> {ticket.equipment}</div>
                            <div><strong>Category:</strong> {ticket.category}</div>
                            <div><strong>Against:</strong> {ticket.against}</div>
                            <div className="um-ticket-date">{ticket.createdAt}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Chat */}
                  {selectedTicket && (
                    <div className="um-chat">
                      <div className="um-chat-header">Chat with Super Admin — Ticket #{selectedTicket.id}</div>
                      <div className="um-chat-messages">
                        {!chatMessages[selectedTicket.id] || chatMessages[selectedTicket.id].length === 0 ? (
                          <div className="um-chat-empty">No messages yet. Start the conversation!</div>
                        ) : (
                          chatMessages[selectedTicket.id].map(msg => (
                            <div key={msg.id} className={`um-chat-msg ${msg.sender === 'User' ? 'sent' : 'received'}`}>
                              <div className={`um-msg-bubble ${msg.sender === 'User' ? 'sent' : 'received'}`}>
                                <div className="um-msg-sender">{msg.sender}</div>
                                <div className="um-msg-text">{msg.message}</div>
                                <div className="um-msg-time">{msg.timestamp}</div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="um-chat-input-area">
                        <input className="um-chat-input" type="text"
                          value={currentMessage}
                          onChange={e => setCurrentMessage(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && handleSendMessage()}
                          placeholder="Type your message..." />
                        <button className="um-chat-send" onClick={handleSendMessage}>Send</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </UserMasterLayout>
  );
};

export default MyMaintenance;